import type BaseLayer from 'ol/layer/Base';
import LayerGroup from 'ol/layer/Group';
import Layer from 'ol/layer/Layer';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import type { BasemapId, HybridReferenceVisibility } from '../core/types';
import { applyHybridReferenceVisibility, createHybridReferenceLayer } from './hybridReference';

const imageryUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

export const esriImageryNativeMaxZoom = 18;
const esriSource = (url: string, attributions: string): XYZ => new XYZ({ url, attributions, crossOrigin: 'anonymous', maxZoom: esriImageryNativeMaxZoom });
const imagerySource = esriSource(imageryUrl, 'Tiles © <a href="https://www.esri.com/">Esri</a>, Vantor, Earthstar Geographics, and the GIS User Community');
const hybridReferenceLayer = createHybridReferenceLayer();

const hybridLayers = [
  new TileLayer({ properties: { basemapRole: 'imagery' }, source: imagerySource }),
  hybridReferenceLayer,
];

export const basemapLayers: Record<Exclude<BasemapId, 'none'>, BaseLayer> = {
  cartographic: new TileLayer({
    properties: { basemapId: 'cartographic' },
    source: new OSM({ crossOrigin: 'anonymous' }),
  }),
  satellite: new TileLayer({
    properties: { basemapId: 'satellite' },
    source: imagerySource,
  }),
  hybrid: new LayerGroup({
    properties: { basemapId: 'hybrid' },
    layers: hybridLayers,
  }),
};

export const setVisibleBasemap = (id: BasemapId): void => {
  Object.entries(basemapLayers).forEach(([basemapId, layer]) => layer.setVisible(id === basemapId));
};

export const setHybridReferenceVisibility = (visibility: HybridReferenceVisibility): void => {
  applyHybridReferenceVisibility(hybridReferenceLayer, visibility);
};

export const refreshBasemapLayers = (): void => {
  const refresh = (layer: BaseLayer): void => {
    if (layer instanceof LayerGroup) layer.getLayers().forEach(refresh);
    else if (layer instanceof Layer) layer.getSource()?.refresh();
  };
  Object.values(basemapLayers).forEach(refresh);
};
